import type {
  WidgetCoreDefinition,
  TeXMLContext,
  TeXMLElement,
  ValidationResult,
  TelnyxEvent,
  CallbackResult,
} from './types';
import { buildCallbackUrl } from './types';

export type HttpHeader = {
  key: string;
  value: string;
};

export type HttpRequestConfig = {
  url: string;
  method: string;
  headers: HttpHeader[];
  body?: string;
  responseVariable: string;
  timeout: number;
};

function generateTeXML(_config: HttpRequestConfig, context: TeXMLContext): TeXMLElement[] {
  const callbackUrl = buildCallbackUrl(context);

  return [
    {
      tag: 'Redirect',
      attributes: {
        method: 'POST',
      },
      content: callbackUrl,
    },
  ];
}

export function httpRequestCallbackHandler(
  _event: TelnyxEvent,
  _config: HttpRequestConfig,
  _variables: Record<string, string>,
): CallbackResult {
  return { nextHandleId: 'error' };
}

function validateConfig(config: HttpRequestConfig): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!config.url.trim()) {
    errors.push('URL is required');
  }

  try {
    new URL(config.url);
  } catch {
    if (config.url.trim()) {
      errors.push('URL must be a valid HTTP/HTTPS URL');
    }
  }

  if (!['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method)) {
    errors.push('Invalid HTTP method');
  }

  if (['POST', 'PUT', 'PATCH'].includes(config.method) && !config.body?.trim()) {
    warnings.push('Request body is empty for a method that typically expects a body');
  }

  if (!config.responseVariable.trim()) {
    errors.push('Response variable name is required');
  }

  if (config.timeout < 1 || config.timeout > 30) {
    errors.push('Timeout must be between 1 and 30 seconds');
  }

  config.headers.forEach((header, index) => {
    if (!header.key.trim()) {
      errors.push(`Header ${index + 1}: Key is required`);
    }
  });

  return { valid: errors.length === 0, errors, warnings };
}

export const httpRequestWidgetCore: WidgetCoreDefinition<HttpRequestConfig> = {
  id: 'http-request',
  version: 1,
  name: 'HTTP Request',
  description: 'Make an HTTP request to an external API',
  category: 'integration',
  tier: 'premium',

  defaultConfig: {
    url: '',
    method: 'GET',
    headers: [],
    responseVariable: 'httpResponse',
    timeout: 10,
  },

  variables: {
    outputs: [
      {
        configKey: 'responseVariable',
        description: 'The HTTP response body',
        type: 'string',
        availableOnHandleTypes: ['success'],
      },
    ],
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: true,
    callbackHandler: httpRequestCallbackHandler,
  },

  validation: {
    validateConfig,
  },
};
